import { ForwardTableStatus } from "@/apis/forward/types";
import { useDSDFiles } from "@/hooks/use-dsd-files";
import { DsdFile } from "@/types/dsd";
import { isBetaFeatureEnabled } from "@/components/beta-feature";
import { isDebugEnabled } from "@/lib/is-debug-enabled";
import { compareTableName } from "@/components/mapping/auto-tag-progress/constants";
import {
  isForwardRunning,
  useEnqueueForwardTasks,
  useForwardStatus,
} from "@/hooks/mutations/use-forward-status";
import { useRollForwardInfo } from "@/hooks/mutations/use-roll-forward-info";
import { useWorkbookId } from "@/hooks/use-workbook";
import queries from "@/queries";
import { existsModal, popModal, pushModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { resolveDisplayState, resolveFailureStage } from "./constants";
import { SelectSourceTableModal } from "./select-source-table-modal";
import { ForwardTableRow, TableSelectView } from "./table-select-view";
import { TableHtmlPreview } from "./table-html-preview";

/**
 * 실패 표에 보여줄 정보. 상세 메시지는 debug 에서만 노출한다 — 오토태깅 패널과 같은
 * 정책으로, 백엔드 예외 문구가 일반 사용자 화면에 새지 않게 한다.
 */
const resolveFailure = (table: ForwardTableStatus | undefined) => {
  const stage = resolveFailureStage(table ?? null);
  if (stage === null) return null;
  return {
    stage,
    message:
      (stage === "AUTO_TAG" ? table?.autoErrorMessage : table?.errorMessage) ??
      null,
  };
};

/**
 * Two-step 모달의 2단계 — 매칭된 표를 섹션 순서로 보여주고, 체크박스로 고른 표만 롤포워드
 * 하며 표별 진행상황을 보여준다(폴링·완료 처리는 페이지 루트의 useForwardCompletionEffect
 * 전담). 시작 여부는 status 응답(record 유무)으로 판정한다.
 */
export const ForwardProgressStep = ({
  sourceWorkbook,
  initialSelectMode,
  currentTableId,
  onPrev,
}: {
  sourceWorkbook?: DsdFile;
  initialSelectMode: "single" | "all" | "none";
  currentTableId?: string;
  onPrev?: () => void;
}) => {
  const { data: workbookId } = useWorkbookId();
  const navigate = useNavigate();
  const { data: info, isLoading: isInfoLoading } = useRollForwardInfo(
    workbookId ?? undefined,
  );
  // 첫 성공 응답 전(폴링 refetch 는 isPending 이 false) — 그 구간만 상태 칩을 스피너로 둔다.
  // 에러도 포함한다: 상태를 못 받은 것과 "아직 안 돌린 표(대기)"는 다르다.
  const {
    data: status,
    isPending,
    isError: isStatusError,
  } = useForwardStatus(workbookId ?? undefined);
  const isStatusLoading = isPending || (isStatusError && !status);
  const { mutate: enqueue, isPending: isEnqueuing } = useEnqueueForwardTasks();
  const { data: dsdFiles } = useDSDFiles();

  // 헤더 요약과 src 표 이름 조회에 쓸 워크북 단위 src — Step1 에서 넘어온 것을 우선하고,
  // 없으면(진행상황 직접 진입/새로고침) roll-forward-info 의 source_workbook_id 로 dsd
  // 목록에서 조회. 한 워크북의 매치는 src 워크북이 하나라는 계약이라(초기화가 매치를
  // 통째로 갈아끼운다) 아무 줄에서 읽어도 같다. 표 미리보기는 이 값이 아니라 줄마다
  // 자기 sourceWorkbookId 를 쓴다.
  const sourceWorkbookId = info?.items?.[0]?.sourceWorkbookId;
  const resolvedFile = useMemo(() => {
    if (sourceWorkbook) return sourceWorkbook;
    if (!sourceWorkbookId) return undefined;
    return dsdFiles?.find((f) => f.id === sourceWorkbookId);
  }, [sourceWorkbook, sourceWorkbookId, dsdFiles]);

  const statusByTable = useMemo(() => {
    const map = new Map<string, ForwardTableStatus>();
    status?.tables.forEach((t) => map.set(t.tableId, t));
    return map;
  }, [status]);

  // 매칭된 표(info) 기준, 섹션 번호순 정렬.
  const rows = useMemo(
    () =>
      [...(info?.items ?? [])]
        .sort((a, b) => compareTableName(a.targetTableName, b.targetTableName))
        .map((item) => {
          const tableStatus = statusByTable.get(item.targetTableId);
          return {
            tableId: item.targetTableId,
            tableName: item.targetTableName,
            // 줄마다 자기 src 워크북을 들고 간다 — 화면 전체를 items[0] 하나로 묶으면
            // 매치에 src 워크북이 섞였을 때 남의 워크북에서 표를 찾게 된다.
            sourceWorkbookId: item.sourceWorkbookId,
            overwriteSrcId: item.overwriteSrcId,
            tagForwardSrcId: item.tagForwardSrcId,
            state: resolveDisplayState(tableStatus ?? null),
            running: tableStatus ? isForwardRunning(tableStatus) : false,
            taggingTarget: item.taggingTarget,
            failure: resolveFailure(tableStatus),
          };
        }),
    [info, statusByTable],
  );

  // 아코디언에서 src 표 이름 표시용 — src 워크북의 표 id → name.
  // 표 목록과 카테고리 응답을 합쳐 커버리지를 넓힌다(카테고리는 수정 모달 캐시 워밍도 겸함).
  const { data: srcTablesData, isLoading: isSrcTablesLoading } = useQuery({
    ...queries.workbook(sourceWorkbookId ?? "")._ctx.registeredTables(),
    enabled: !!sourceWorkbookId,
  });
  const { data: srcCategoriesData, isLoading: isSrcCategoriesLoading } =
    useQuery({
      ...queries.workbook(sourceWorkbookId ?? "")._ctx.categories(),
      enabled: !!sourceWorkbookId,
    });
  const isSrcNamesLoading = isSrcTablesLoading || isSrcCategoriesLoading;
  const srcTableNameById = useMemo(() => {
    const map = new Map<string, string>();
    srcTablesData?.tables.forEach((t) => map.set(t.id, t.name));
    srcCategoriesData?.categories.forEach((c) =>
      c.tables.forEach((t) => map.set(t.id, t.name)),
    );
    return map;
  }, [srcTablesData, srcCategoriesData]);

  // 줄의 바로가기에 필요한 tableId → categoryId 매핑(이월 대상, 즉 현재 워크북 기준).
  // 라우트(/mapping/:categoryId)·시트 로더와 같은 categories 쿼리의 id 를 써야 매칭된다
  // (tableAddresses 의 categoryId 는 id 체계가 달라 매칭 실패).
  const { data: categories } = useQuery({
    ...queries.workbook(workbookId ?? "")._ctx.categories(),
    enabled: !!workbookId,
    select: (d) => d.categories,
  });
  const categoryIdByTable = useMemo(() => {
    const map = new Map<string, string>();
    categories?.forEach((c) => c.tables.forEach((t) => map.set(t.id, c.id)));
    return map;
  }, [categories]);

  // 그 표가 어느 category 에 있는지 알 때만 바로가기를 준다(카테고리 로딩 전에는 없음).
  // ?table= 은 시트 로더가 읽는 SSOT — 카테고리를 건너뛰어도 해당 시트로 열린다.
  const resolveGoToTable = (row: ForwardTableRow) => {
    const categoryId = categoryIdByTable.get(row.tableId);
    if (!workbookId || !categoryId) return undefined;
    return () => {
      navigate(`/mapping/${workbookId}/${categoryId}?table=${row.tableId}`);
      popModal();
    };
  };

  const [expandedId, setExpandedId] = useState<string | null>(null);

  // 스마트 롤포워드(beta): 태그를 이월한 뒤 태깅이 부족한 표만 골라 직전 기를
  // reference 로 오토태깅까지 이어서 돌린다. beta feature 로만 노출.
  const betaEnabled = isBetaFeatureEnabled();
  const [smartRollForward, setSmartRollForward] = useState(false);

  // 선택 상태 — info 로드 후 initialSelectMode 로 1회 초기화.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current || !info) return;
    initializedRef.current = true;
    // 일반사항·회계정책·추정 등 목차의 표는 "전체 표 불러오기"로 열어도 처음부터 체크되지 않는다.
    const targets = info.items.filter((i) => i.taggingTarget);
    const ids =
      initialSelectMode === "none"
        ? []
        : initialSelectMode === "single" && currentTableId
          ? targets
              .filter((i) => i.targetTableId === currentTableId)
              .map((i) => i.targetTableId)
          : targets.map((i) => i.targetTableId);
    setSelectedIds(new Set(ids));
  }, [info, initialSelectMode, currentTableId]);

  const toggleSelect = (tableId: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(tableId)) next.delete(tableId);
      else next.add(tableId);
      return next;
    });
  // 진행 중(PENDING/RUNNING)인 표는 다시 못 고른다. 미실행·완료·실패 표는 선택 가능
  // — 진행 중인 표를 기다리지 않고 나머지를 골라 바로 추가 실행할 수 있다.
  // 일반사항·회계정책·추정 등 목차의 표(taggingTarget=false)는 실행되지 않으므로 전체 선택에서도 뺀다.
  const selectableRows = rows.filter((r) => !r.running && r.taggingTarget);
  const selectedRunnableIds = selectableRows
    .filter((r) => selectedIds.has(r.tableId))
    .map((r) => r.tableId);
  const allSelected =
    selectableRows.length > 0 &&
    selectedRunnableIds.length === selectableRows.length;
  const toggleSelectAll = () =>
    setSelectedIds(
      allSelected ? new Set() : new Set(selectableRows.map((r) => r.tableId)),
    );

  const renderExpanded = (row: ForwardTableRow) => {
    const item = rows.find((r) => r.tableId === row.tableId);
    if (!item) return null;
    // overwrite/tag src 가 같으면(대부분) 한 블록, 다르면 참조별로 나눠 보여준다.
    const srcSections =
      item.overwriteSrcId === item.tagForwardSrcId
        ? [{ label: "매칭된 표", srcId: item.overwriteSrcId }]
        : [
            { label: "표 구조 참조", srcId: item.overwriteSrcId },
            { label: "XBRL 태그 참조", srcId: item.tagForwardSrcId },
          ];

    return (
      <div className="flex flex-col gap-3 border-t border-line-normal px-3 py-3">
        {item.failure && (
          <div className="rounded-[6px] bg-red-50 px-2 py-1.5 caption-1-medium text-red-600">
            {item.failure.stage === "AUTO_TAG"
              ? "태그 이월은 됐지만 이어진 오토태깅에서 에러가 발생했어요. 이 표는 직접 태깅해 주세요."
              : "태그를 불러오는 중 에러가 발생했어요."}
            {isDebugEnabled() && item.failure.message && (
              <span className="mt-1 block break-all caption-2-regular">
                {item.failure.message}
              </span>
            )}
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="shrink-0 caption-2-medium text-label-alternative">
              현재 표
            </span>
            <span
              className="min-w-0 flex-1 truncate caption-1-medium text-label-normal"
              title={item.tableName}
            >
              {item.tableName}
            </span>
            <Button
              variant="outlined"
              size="small"
              className="shrink-0"
              disabled={item.running || !item.sourceWorkbookId}
              onClick={() => {
                if (!item.sourceWorkbookId) return;
                // 더블클릭 등으로 같은 모달이 스택에 중복 push 되는 것을 방지.
                const modalId = `select-source-table-${item.tableId}`;
                if (existsModal(modalId)) return;
                pushModal(SelectSourceTableModal, {
                  id: modalId,
                  targetTableId: item.tableId,
                  targetTableName: item.tableName,
                  sourceWorkbookId: item.sourceWorkbookId,
                  currentSourceTableId:
                    item.tagForwardSrcId ?? item.overwriteSrcId,
                });
              }}
            >
              수정
            </Button>
          </div>
          {workbookId && (
            <TableHtmlPreview
              workbookId={workbookId}
              tableId={item.tableId}
              scaleFactor={0.7}
              className="max-h-[240px] rounded-[6px] border border-line-normal p-2"
            />
          )}
        </div>
        {srcSections.map((section) => {
          const srcName = section.srcId
            ? srcTableNameById.get(section.srcId)
            : undefined;
          return (
            <div key={section.label} className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <span className="shrink-0 caption-2-medium text-label-alternative">
                  {section.label}
                </span>
                <span
                  className="min-w-0 flex-1 truncate caption-1-medium text-label-normal"
                  title={srcName}
                >
                  {section.srcId
                    ? (srcName ??
                      (isSrcNamesLoading
                        ? "이름 불러오는 중..."
                        : section.srcId))
                    : "매칭 없음"}
                </span>
              </div>
              {section.srcId && item.sourceWorkbookId && (
                <TableHtmlPreview
                  workbookId={item.sourceWorkbookId}
                  tableId={section.srcId}
                  scaleFactor={0.7}
                  className="max-h-[240px] rounded-[6px] border border-line-normal p-2"
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <TableSelectView
      sourceFile={resolvedFile}
      isLoading={isInfoLoading}
      isStatusLoading={isStatusLoading}
      rows={rows}
      selectedIds={selectedIds}
      allSelected={allSelected}
      selectedCount={selectedRunnableIds.length}
      isSelectAllDisabled={selectableRows.length === 0}
      onToggleRow={toggleSelect}
      onToggleAll={toggleSelectAll}
      expandedId={expandedId}
      onToggleExpand={(tableId) =>
        setExpandedId(expandedId === tableId ? null : tableId)
      }
      renderExpanded={renderExpanded}
      resolveGoToTable={resolveGoToTable}
      smartRollForward={smartRollForward}
      onSmartRollForwardChange={setSmartRollForward}
      onPrev={onPrev}
      isStarting={isEnqueuing}
      onStart={() =>
        enqueue(
          {
            tableIds: selectedRunnableIds,
            smart: betaEnabled && smartRollForward,
          },
          { onSuccess: () => setSelectedIds(new Set()) },
        )
      }
    />
  );
};
